import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SearchParams from '@/_constants/SearchParams.ts';
import { useDefaultLayoutContext } from '@/layouts/Default/DefaultLayoutProvider.tsx';
import Chat from '@/components/functional/Chat/Chat.tsx';
import useChats from '@/api/chat/useChats.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import Default from '@/layouts/Default/Default.tsx';
import { Trans, useTranslation } from 'react-i18next';
import {
  PaperClipIcon,
  Cog6ToothIcon,
  ClockIcon,
  ArrowPathIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { Alert, Button, Tooltip } from 'react-daisyui';
import useResizeObserver from 'use-resize-observer';
import SearchResult, { NoResults } from '@/components/ui/SearchResult/SearchResult.tsx';
import { useSearchContext } from '@/context/SearchContext.tsx';
import Routes from '@/router/Routes.ts';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import UserContextModal from '@/components/modals/UserContextModal/UserContextModal.tsx';
import RecallSource from '@/_constants/RecallSource.ts';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';

const HomePage: FC = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState<boolean>(true);
  const { setMenuOptions, containerElementRef, setIsLoadingMenu, setIsDrawerOpen, isDrawerOpen } =
    useDefaultLayoutContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, hasAiResponsesLimitFeature, hasSearchLimitFeature, availableAiResponses, availableSearches } =
    useAuthContext();
  const {
    pinnedItems,
    searchHistory,
    togglePinItem,
    storageConsent,
    isLoadingSearchHistory,
    errorSearchHistory,
    isLoadingPinnedItems,
    errorPinnedItems,
    activeChatId,
    setActiveChatId,
    isLoadingUserContext,
    localUserContext,
    recall,
    isLoadingChatWithBot,
  } = useSearchContext();
  const { data: chats, isLoading: isLoadingChats } = useChats(true, { user_id: user?.id });
  const { ref, width } = useResizeObserver<HTMLDivElement>();
  const [openedDrawerContent, setOpenedDrawerContent] = useState<'pinned_items' | 'search_history' | null>(null);
  const [isContextModalOpen, setIsContextModalOpen] = useState<boolean>(false);

  const activeChatIdFromUrl = searchParams.get(SearchParams.CHAT_ID);

  useEffect(() => {
    setActiveChatId(activeChatIdFromUrl);

    return () => setActiveChatId(null);
  }, [activeChatIdFromUrl, setActiveChatId]);

  const selectElement = useCallback(
    (id: string) => {
      setSearchParams((prev) => {
        prev.set(SearchParams.CHAT_ID, id);
        return prev;
      });
    },
    [setSearchParams]
  );

  const activeChat = useMemo(() => {
    return chats?.find((chat) => chat.id === activeChatId);
  }, [chats, activeChatId]);

  const scrollChatFn = useCallback(() => {
    if (!containerElementRef.current) return;

    containerElementRef.current.scrollTop = containerElementRef.current.scrollHeight;
    window.scrollTo(0, containerElementRef.current.scrollHeight);
  }, [containerElementRef]);

  const handleTogglePinnedItems = useCallback(() => {
    if (!isDrawerOpen) {
      setOpenedDrawerContent('pinned_items');
      setIsDrawerOpen(true);
      return;
    }

    setIsDrawerOpen(false);
    setOpenedDrawerContent(null);
  }, [setIsDrawerOpen, isDrawerOpen]);

  const handleToggleSearchHistory = useCallback(() => {
    if (!isDrawerOpen) {
      setOpenedDrawerContent('search_history');
      setIsDrawerOpen(true);
      return;
    }
    setIsDrawerOpen(false);
    setOpenedDrawerContent(null);
  }, [setIsDrawerOpen, isDrawerOpen]);

  useEffect(() => {
    setIsLoadingMenu(isLoadingChats);
  }, [isLoadingChats, setIsLoadingMenu]);

  useEffect(() => {
    if (!chats || isLoadingChats) return;
    setMenuOptions(chats.map((chat) => ({ id: chat.id, name: chat.name })) || []);
    if (chats.length > 0 && !activeChatId) {
      selectElement(chats[0].id);
    }
  }, [activeChatId, chats, isLoadingChats, selectElement, setMenuOptions]);

  const elementToRecall = useMemo(() => {
    const lastPinnedItem = pinnedItems[0]?.timestamp ?? 0;
    const lastSearchHistoryItem = searchHistory[0]?.timestamp ?? 0;

    if (lastPinnedItem === 0 && lastSearchHistoryItem === 0) {
      return null;
    }

    if (lastPinnedItem > lastSearchHistoryItem) {
      return {
        source: RecallSource.PINNED,
        search_result: pinnedItems[0],
      };
    }

    if (lastSearchHistoryItem > lastPinnedItem) {
      return {
        source: RecallSource.LAST_SEARCH,
        search_result: searchHistory[0],
      };
    }

    if (lastPinnedItem === lastSearchHistoryItem) {
      return {
        source: RecallSource.PINNED,
        search_result: pinnedItems[0],
      };
    }

    return null;
  }, [pinnedItems, searchHistory]);

  const handleRecall = () => {
    void (async () => {
      try {
        if (isLoadingChatWithBot) return;
        if (!elementToRecall) return;

        await recall({
          source: elementToRecall.source,
          search_result: elementToRecall.search_result,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  useLayoutEffect(() => {
    if (!width) return;

    if (width > 1023) {
      setIsMobile(false);
      return;
    }

    setIsMobile(true);
  }, [width]);

  useEffect(() => {
    if (!isLoadingUserContext && !isLoadingChats && !!activeChatId && !localUserContext) {
      setIsContextModalOpen(true);
      return;
    }

    setIsContextModalOpen(false);
  }, [isLoadingUserContext, isLoadingChats, activeChatId, localUserContext]);

  return (
    <>
      {isContextModalOpen && !!activeChatId && !isLoadingUserContext ? (
        <UserContextModal
          key={activeChatId}
          isOpen={isContextModalOpen}
          chatId={activeChatId}
          onClose={() => setIsContextModalOpen(false)}
          context={localUserContext}
        />
      ) : null}
      <div className="flex grow justify-center">
        <div className="w-[800px] max-w-full flex">
          {activeChat ? (
            <Chat
              scrollChatFn={scrollChatFn}
              focus
              chat={activeChat}
              hasAiResponsesLimitFeature={hasAiResponsesLimitFeature}
              hasSearchLimitFeature={hasSearchLimitFeature}
              hasSubscription={!!user?.is_subscription_active}
            />
          ) : null}
        </div>
        <Default.TopSticky>
          <div ref={ref} className="flex flex-col lg:gap-6">
            {!user?.is_subscription_active ? (
              <div className="flex flex-wrap justify-center gap-x-5 p-2 bg-white shadow-md pointer-events-auto">
                <div className="flex gap-1 items-end">
                  <div className="font-mono text-xl lg:text-2xl text-primary-400 font-bold">{availableAiResponses}</div>
                  <div className="text-sm lg:text-base pb-[2px] lg:pb-[1px]">{t('other.aiResponsesLeft')}</div>
                </div>
                <div className="flex gap-1 items-end">
                  <div className="font-mono text-xl lg:text-2xl text-primary-400 font-bold">{availableSearches}</div>
                  <div className="text-sm lg:text-base pb-[2px] lg:pb-[1px]">{t('other.searchesLeft')}</div>
                </div>
              </div>
            ) : null}
            <div className="flex justify-end pt-4 pr-2 lg:pr-4">
              <div className="pointer-events-auto flex flex-col gap-2 lg:gap-4">
                <Tooltip message={t('button.settings')} position="left">
                  <Link to={Routes.SETTINGS__CONSENTS}>
                    <Button
                      className="drop-shadow-lg"
                      size={isMobile ? 'md' : 'lg'}
                      color="accent"
                      shape="circle"
                      aria-label={t('button.settings')}
                    >
                      <Cog6ToothIcon className={isMobile ? 'w-5 h-5' : 'w-8 h-8'} />
                    </Button>
                  </Link>
                </Tooltip>
                <Tooltip message={t('button.chat_context')} position="left">
                  <Button
                    className="drop-shadow-lg"
                    size={isMobile ? 'md' : 'lg'}
                    color="accent"
                    shape="circle"
                    aria-label={t('button.chat_context')}
                    onClick={() => setIsContextModalOpen(true)}
                  >
                    <ChatBubbleBottomCenterTextIcon className={isMobile ? 'w-5 h-5' : 'w-8 h-8'} />
                  </Button>
                </Tooltip>
                <Tooltip message={t('button.pinned_items')} position="left">
                  <Button
                    className="drop-shadow-lg"
                    size={isMobile ? 'md' : 'lg'}
                    color="accent"
                    shape="circle"
                    aria-label={t('button.pinned_items')}
                    onClick={handleTogglePinnedItems}
                  >
                    <PaperClipIcon className={isMobile ? 'w-5 h-5' : 'w-8 h-8'} />
                  </Button>
                </Tooltip>
                <Tooltip message={t('button.search_history')} position="left">
                  <Button
                    className="drop-shadow-lg"
                    size={isMobile ? 'md' : 'lg'}
                    color="accent"
                    shape="circle"
                    aria-label={t('button.search_history')}
                    onClick={handleToggleSearchHistory}
                  >
                    <ClockIcon className={isMobile ? 'w-5 h-5' : 'w-8 h-8'} />
                  </Button>
                </Tooltip>
                <Tooltip message={t('button.recall')} position="left">
                  <LoadingButton
                    loading={isLoadingChatWithBot}
                    disabled={!elementToRecall}
                    className="drop-shadow-lg"
                    size={isMobile ? 'md' : 'lg'}
                    color="accent"
                    shape="circle"
                    aria-label={t('button.recall')}
                    onClick={handleRecall}
                  >
                    {!isLoadingChatWithBot ? <ArrowPathIcon className={isMobile ? 'w-5 h-5' : 'w-8 h-8'} /> : null}
                  </LoadingButton>
                </Tooltip>
              </div>
            </div>
          </div>
        </Default.TopSticky>
        <Default.Drawer>
          <LoaderWrapper
            isLoading={isLoadingSearchHistory || isLoadingPinnedItems}
            className="max-w-full w-[430px] h-[calc(100dvh-96px)]"
          >
            {errorSearchHistory ? <Alert status="warning">{errorSearchHistory.message}</Alert> : null}
            {errorPinnedItems ? <Alert status="warning">{errorPinnedItems.message}</Alert> : null}
            {openedDrawerContent === 'pinned_items' ? (
              <div className="flex flex-col gap-6 pt-4 w-[430px] max-w-full">
                {pinnedItems.length ? (
                  pinnedItems.map((result) => (
                    <SearchResult
                      key={`pinned_item_${result.id}`}
                      phrase={result.phrase}
                      tld_pref={result.tld_pref}
                      domains={result.domains}
                      socialMedia={result.socialMedia}
                      onPinClick={() => togglePinItem(result.id)}
                      isPinned={true}
                    />
                  ))
                ) : (
                  <NoResults>
                    <div className="flex flex-col gap-4">
                      <span>{t('other.no_pinned_items')} </span>
                      {!storageConsent && (
                        <span>
                          <Trans i18nKey="other.keep_saved_info">
                            To keep them between sessions, update your storage consent on
                            <Link className="font-bold text-primary-400" to={Routes.SETTINGS__CONSENTS}>
                              settings page
                            </Link>
                            .
                          </Trans>
                        </span>
                      )}
                    </div>
                  </NoResults>
                )}
              </div>
            ) : null}
            {openedDrawerContent === 'search_history' ? (
              <div className="flex flex-col gap-6 pt-4 w-[430px] max-w-full">
                {searchHistory.length ? (
                  searchHistory.map((result) => (
                    <SearchResult
                      key={`search_history_item_${result.id}`}
                      phrase={result.phrase}
                      tld_pref={result.tld_pref}
                      domains={result.domains}
                      socialMedia={result.socialMedia}
                    />
                  ))
                ) : (
                  <NoResults>
                    <div className="flex flex-col gap-4">
                      <span>{t('other.no_search_history_items')} </span>
                      {!storageConsent && (
                        <span>
                          <Trans i18nKey="other.keep_saved_info">
                            To keep them between sessions, update your storage consent on
                            <Link className="font-bold text-primary-400" to={Routes.SETTINGS__CONSENTS}>
                              settings page
                            </Link>
                            .
                          </Trans>
                        </span>
                      )}
                    </div>
                  </NoResults>
                )}
              </div>
            ) : null}
          </LoaderWrapper>
        </Default.Drawer>
      </div>
    </>
  );
};

export default HomePage;
